import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { Link, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';

// ─── Animations ───────────────────────────────────────────────────────────────

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(14px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

// ─── Styled Components ────────────────────────────────────────────────────────

const Page = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #0d0d0d;
  padding: 40px 24px;
`;

const Card = styled.div`
  background: #1a1a1a;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 20px;
  padding: 52px 48px;
  width: 100%;
  max-width: 460px;
  text-align: center;
  animation: ${fadeIn} 0.4s ease;
`;

const Logo = styled.h1`
  color: #fff;
  font-size: 20px;
  font-weight: 800;
  margin: 0 0 36px;
  letter-spacing: -0.5px;
  span { color: #fe88f1; }
`;

const IconWrap = styled.div`
  font-size: 56px;
  margin-bottom: 24px;
  line-height: 1;
`;

const Heading = styled.h2`
  color: #fff;
  font-size: 1.4rem;
  font-weight: 700;
  margin: 0 0 12px;
`;

const SubText = styled.p`
  color: rgba(255, 255, 255, 0.55);
  font-size: 0.95rem;
  line-height: 1.6;
  margin: 0 0 32px;
`;

const Spinner = styled.div`
  width: 44px;
  height: 44px;
  border: 3px solid rgba(255, 255, 255, 0.1);
  border-top-color: #fe88f1;
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
  margin: 0 auto 28px;
`;

const Btn = styled(Link)`
  display: inline-block;
  padding: 13px 36px;
  background: #fe88f1;
  color: #0d0d0d;
  font-size: 0.95rem;
  font-weight: 700;
  border-radius: 8px;
  text-decoration: none;
  transition: opacity 0.2s, transform 0.15s;
  &:hover { opacity: 0.88; transform: translateY(-1px); }
`;

// ─── Component ────────────────────────────────────────────────────────────────

const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [status, setStatus] = useState('loading'); // 'loading' | 'success' | 'error' | 'notoken'

  useEffect(() => {
    if (!token) {
      setStatus('notoken');
      return;
    }
    axios
      .get(`${BACKEND_URL}/verify-email`, { params: { token } })
      .then(() => setStatus('success'))
      .catch(() => setStatus('error'));
  }, [token]);

  return (
    <Page>
      <Helmet>
        <title>Verify Email — MyCinemaDB</title>
        <meta name="description" content="Verify your MyCinemaDB email address to start saving films and getting recommendations." />
      </Helmet>

      <Card>
        <Logo>My<span>Cinema</span>DB</Logo>

        {status === 'loading' && (
          <>
            <Spinner />
            <Heading>Verifying your email…</Heading>
            <SubText>Hang tight, this just takes a second.</SubText>
          </>
        )}

        {status === 'success' && (
          <>
            <IconWrap>✅</IconWrap>
            <Heading>Email verified!</Heading>
            <SubText>Your email address has been confirmed. You can now sign in and start exploring films.</SubText>
            <Btn to="/sign-in">Sign In</Btn>
          </>
        )}

        {status === 'error' && (
          <>
            <IconWrap>⚠️</IconWrap>
            <Heading>Verification failed</Heading>
            <SubText>
              This link may have expired or already been used. Request a new one from the sign-in page.
            </SubText>
            <Btn to="/sign-in">Go to Sign In</Btn>
          </>
        )}

        {status === 'notoken' && (
          <>
            <IconWrap>📬</IconWrap>
            <Heading>Check your inbox</Heading>
            <SubText>
              We've sent you a verification email. Click the link inside to activate your account.
            </SubText>
            <Btn to="/sign-in">Back to Sign In</Btn>
          </>
        )}
      </Card>
    </Page>
  );
};

export default VerifyEmail;
